/* eslint-disable max-lines-per-function */
import { useRef, useState } from 'react';
import { Button, Dialog, Snackbar } from '@equinor/eds-core-react';
import { analogueModelDefault } from '../../../../constants/analogueModelDefault';
import { useFetchModel } from '../../../../hooks/useFetchModel';
import { useIsOwnerOrAdmin } from '../../../../hooks/useIsOwnerOrAdmin';
import { ArchelCanvas } from '../ArchelCanvas/ArchelCanvas';
import { ArchelProportions } from '../ArchelProportions/ArchelProportions';
import { AreaForm } from '../AreaForm/AreaForm';
import { useArchelGrid } from '../hooks/useArchelGrid';
import {
  useArchelBboxProportions,
  useArchelWholeModelProportions,
} from '../hooks/useArchelProportions';
import {
  createModelAreasStore,
  ModelAreasStoreContext,
  useModelAreasStore,
} from '../hooks/useModelAreasStore';
import { buildArchelMeta } from '../modelAreaCoordinates.utils';
import * as Styled from './ModelAreasDialog.styled';
import { useFetchGrossDepData } from '../../../../hooks/useFetchGrossDepData';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ModelAreasDialog = ({ open, onClose }: Props) => {
  const storeRef = useRef(createModelAreasStore());

  const handleClose = () => {
    storeRef.current.getState().reset();
    onClose();
  };

  return (
    <ModelAreasStoreContext.Provider value={storeRef.current}>
      <DialogContent open={open} onClose={handleClose} />
    </ModelAreasStoreContext.Provider>
  );
};

interface DialogContentProps {
  open: boolean;
  onClose: () => void;
}

const DialogContent = ({ open, onClose }: DialogContentProps) => {
  const { data: analogueModel = analogueModelDefault } = useFetchModel();

  const { data: geologyStandards } = useFetchGrossDepData();

  const isOwnerOrAdmin = useIsOwnerOrAdmin();
  const [savedAlert, setSavedAlert] = useState(false);

  const modelId = analogueModel.isProcessed
    ? analogueModel.analogueModelId || undefined
    : undefined;
  const archels = buildArchelMeta(
    analogueModel.iniParameters?.archels ?? [],
    geologyStandards,
  );
  const gridQuery = useArchelGrid(modelId);
  const wholeProps = useArchelWholeModelProportions(modelId);
  const bbox = useModelAreasStore((s) => s.bbox);
  const bboxProps = useArchelBboxProportions(modelId, bbox);

  return (
    <>
      <Styled.StyledDialog open={open} isDismissable onClose={onClose}>
        <Dialog.Header>
          <Dialog.Title>Model areas — {analogueModel.name}</Dialog.Title>
        </Dialog.Header>
        <Styled.Content>
          <Styled.ThreeColumnRow>
            {isOwnerOrAdmin && (
              <Styled.FormColumn>
                <AreaForm onSaved={() => setSavedAlert(true)} />
              </Styled.FormColumn>
            )}
            <ArchelCanvas
              gridData={gridQuery.data}
              loading={gridQuery.isLoading}
              error={gridQuery.error ? String(gridQuery.error) : null}
              archels={archels}
            />
            <ArchelProportions
              wholeModel={wholeProps.data}
              selected={bboxProps.data}
              loading={bboxProps.isFetching}
              archels={archels}
            />
          </Styled.ThreeColumnRow>
        </Styled.Content>
        <Dialog.Actions>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Dialog.Actions>
      </Styled.StyledDialog>

      <Snackbar
        open={savedAlert}
        onClose={() => setSavedAlert(false)}
        autoHideDuration={3000}
      >
        Coordinates saved
      </Snackbar>
    </>
  );
};
